import React, { useState } from 'react';
import { Card, Button, Badge, Modal, Input } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';
import CodeManager from '../components/domain/CodeManager';

const EMPTY = {
  nombre_completo: '',
  documento: '',
  telefono: '',
  email: '',
  direccion: '',
  tipo: 'particular',
  estado: 'activo',
  notas: ''
};

const Clientes = () => {
  const { data, loading, error, refresh, insert, update, remove } = useDatabase('clientes');
  
  // Traemos pagos, reservas y trasteros para el historial
  const { data: pagos } = useDatabase('pagos');
  const { data: reservas } = useDatabase('reservas');
  const { data: trasteros } = useDatabase('trasteros');

  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para el Modal de Edición/Creación
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Estados para el Modal de Historial
  const [historyClientId, setHistoryClientId] = useState(null);

  const filtered = data.filter(c => 
    c.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.documento?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      nombre_completo: item.nombre_completo,
      documento: item.documento,
      telefono: item.telefono || '',
      email: item.email || '',
      direccion: item.direccion || '',
      tipo: item.tipo,
      estado: item.estado,
      notas: item.notas || ''
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const openHistory = (id) => {
    setHistoryClientId(id);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await update(editingId, form);
      else await insert(form);
      
      setIsModalOpen(false);
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este cliente?')) return;
    try {
      await remove(id);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // --- Lógica de cálculo para el Historial ---
  const activeClient = data.find(c => c.id === historyClientId);
  const clientPagos = pagos.filter(p => p.cliente_id === historyClientId);
  const clientReservas = reservas.filter(r => r.cliente_id === historyClientId);

  const totalPagado = clientPagos.filter(p => p.estado === 'pagado').reduce((acc, p) => acc + Number(p.importe), 0);
  const totalPendiente = clientPagos.filter(p => p.estado === 'pendiente' || p.estado === 'atrasado').reduce((acc, p) => acc + Number(p.importe), 0);

  // Buscar primer pago (ordenando los pagados por fecha)
  const pagosRealizados = clientPagos.filter(p => p.estado === 'pagado' && p.fecha_pago).sort((a, b) => new Date(a.fecha_pago) - new Date(b.fecha_pago));
  const primerPago = pagosRealizados.length > 0 ? new Date(pagosRealizados[0].fecha_pago).toLocaleDateString() : '—';

  // Buscar próximo pago (ordenando los pendientes por fecha de vencimiento)
  const pagosPendientes = clientPagos.filter(p => (p.estado === 'pendiente' || p.estado === 'atrasado') && p.fecha_vencimiento).sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));
  const proximoPago = pagosPendientes.length > 0 ? new Date(pagosPendientes[0].fecha_vencimiento).toLocaleDateString() : '—';

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark flex items-center gap-2">
            Directorio de Clientes
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-sm sm:text-base text-brand-text">Gestiona la información de tus inquilinos.</p>
        </div>
        <Button onClick={openNew} className="w-full sm:w-auto flex justify-center items-center gap-2">
          <SafeIcon icon={FiIcons.FiPlus} />
          Nuevo Cliente
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 text-sm">
          <SafeIcon icon={FiIcons.FiAlertTriangle} /><span>Error: {error}</span>
        </div>
      )}

      <Card>
        <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative w-full sm:max-w-md">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Buscar por nombre o documento..." 
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-primary"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="ghost" onClick={refresh} className="w-full sm:w-auto ml-auto sm:ml-0 flex justify-center">
            <SafeIcon icon={FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <div className="overflow-x-auto hide-scrollbar">
          {!loading && filtered.length === 0 ? (
            <p className="text-center text-brand-text py-12 text-sm">No hay clientes registrados.</p>
          ) : (
            <table className="w-full text-left text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  <th className="px-6 py-3 font-medium">Nombre / Empresa</th>
                  <th className="px-6 py-3 font-medium">Documento</th>
                  <th className="px-6 py-3 font-medium">Contacto</th>
                  <th className="px-6 py-3 font-medium">Estado</th>
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-brand-dark">{item.nombre_completo}</td>
                    <td className="px-6 py-4 text-brand-text">{item.documento}</td>
                    <td className="px-6 py-4 text-brand-text">
                      <div className="flex flex-col">
                        <span>{item.telefono}</span>
                        <span className="text-xs text-gray-400">{item.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4"><Badge variant={item.estado}>{item.estado}</Badge></td>
                    <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                      <button className="text-gray-400 hover:text-brand-accent p-2 sm:p-1" title="Ver Historial" onClick={() => openHistory(item.id)}>
                        <SafeIcon icon={FiIcons.FiClock} />
                      </button>
                      <button className="text-gray-400 hover:text-brand-primary p-2 sm:p-1" title="Editar" onClick={() => openEdit(item)}>
                        <SafeIcon icon={FiIcons.FiEdit2} />
                      </button>
                      <button className="text-gray-400 hover:text-brand-alert p-2 sm:p-1" title="Eliminar" onClick={() => handleDelete(item.id)}>
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Modal de Creación/Edición */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }}
        title={editingId ? 'Ficha de Cliente' : 'Nuevo Cliente'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Nombre completo" placeholder="Carlos Sánchez López" value={form.nombre_completo} onChange={e => setForm({...form, nombre_completo: e.target.value})} required />
            <Input label="DNI / NIE / CIF" placeholder="12345678A" value={form.documento} onChange={e => setForm({...form, documento: e.target.value})} required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Teléfono" placeholder="600111222" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} />
            <Input label="Email" type="email" placeholder="cliente@email.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          </div>

          <Input label="Dirección" placeholder="Calle Mayor 1, Madrid" value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Tipo</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                <option value="particular">Particular</option>
                <option value="autónomo">Autónomo</option>
                <option value="empresa">Empresa</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Estado</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.estado} onChange={e => setForm({...form, estado: e.target.value})}>
                <option value="activo">Activo</option>
                <option value="potencial">Potencial</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-1">Notas</label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary text-sm" rows={2} value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} />
          </div>

          {editingId && (
            <div className="pt-4 border-t border-gray-100">
              <h4 className="text-sm font-medium text-brand-dark mb-3">Acceso al Centro</h4>
              <CodeManager clienteId={editingId} />
            </div>
          )}

          <div className="pt-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-gray-100 mt-4">
            <Button variant="ghost" onClick={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }} type="button" className="w-full sm:w-auto">Cerrar</Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Guardando...' : editingId ? 'Actualizar Datos' : 'Guardar Cliente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Historial */}
      <Modal 
        isOpen={!!historyClientId} 
        onClose={() => setHistoryClientId(null)}
        title={activeClient ? `Historial: ${activeClient.nombre_completo}` : 'Historial'}
      >
        <div className="space-y-6">
          
          {/* Tarjetas de Resumen Contable */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-xs text-brand-text mb-1">Total Pagado</p>
              <p className="text-lg font-bold text-brand-accent">€{totalPagado.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-xs text-brand-text mb-1">Total Pendiente</p>
              <p className="text-lg font-bold text-brand-alert">€{totalPendiente.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-xs text-brand-text mb-1">Primer Pago</p>
              <p className="text-sm font-semibold text-brand-dark">{primerPago}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-xs text-brand-text mb-1">Próximo Pago</p>
              <p className="text-sm font-semibold text-brand-dark">{proximoPago}</p>
            </div>
          </div>

          {/* Reservas Activas */}
          <div>
            <h4 className="text-sm font-bold text-brand-dark mb-2 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiBox} /> Trasteros Alquilados
            </h4>
            {clientReservas.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No tiene reservas registradas.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {clientReservas.map(res => {
                  const trastero = trasteros.find(t => t.id === res.trastero_id);
                  return (
                    <div key={res.id} className="text-sm p-3 border border-gray-200 rounded-lg flex justify-between items-center">
                      <span className="font-medium">{trastero ? `${trastero.codigo} - ${trastero.zona}` : 'Trastero Desconocido'}</span>
                      <Badge variant={res.estado}>{res.estado}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Historial de Pagos */}
          <div>
            <h4 className="text-sm font-bold text-brand-dark mb-2 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiList} /> Historial de Pagos
            </h4>
            <div className="border border-gray-200 rounded-lg overflow-x-auto hide-scrollbar max-h-60">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-brand-text sticky top-0">
                  <tr>
                    <th className="px-4 py-2 font-medium">Concepto</th>
                    <th className="px-4 py-2 font-medium">Importe</th>
                    <th className="px-4 py-2 font-medium">Vencimiento</th>
                    <th className="px-4 py-2 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {clientPagos.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-gray-500 py-4 italic">Sin pagos registrados.</td>
                    </tr>
                  ) : (
                    clientPagos.sort((a,b) => new Date(b.fecha_vencimiento || 0) - new Date(a.fecha_vencimiento || 0)).map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-brand-dark">{p.concepto}</td>
                        <td className="px-4 py-2 font-medium">€{Number(p.importe).toFixed(2)}</td>
                        <td className="px-4 py-2 text-brand-text">{p.fecha_vencimiento ? new Date(p.fecha_vencimiento).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-2"><Badge variant={p.estado}>{p.estado}</Badge></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-4 flex justify-end border-t border-gray-100">
            <Button variant="ghost" onClick={() => setHistoryClientId(null)} className="w-full sm:w-auto">Cerrar Historial</Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Clientes;